import {
	NAVIGATION_PAGE_TYPE,
	SYSTEM_LOCALE,
	ELEMENT_TYPE_BUTTON,
	ELEMENT_TYPE_SOCIAL_ICONS,
} from '@zyro-inc/site-modules/constants/siteModulesConstants';
import { objectToCssVariablesDeprecated } from '@zyro-inc/site-modules/utils/objectToCssVariablesDeprecated';
import { getBackgroundStyles } from '@zyro-inc/site-modules/utils/getBackgroundStyles';
import { getOptimizedSrc } from '@zyro-inc/site-modules/utils/getSrcsets';
import { FULL_WIDTH } from '@zyro-inc/site-modules/utils/getGridItemSize';

export const getHeaderProps = ({
	siteId,
	meta,
	blocks,
	nav,
	pages,
	elements,
	languageMetaTitle,
	languageSwitcherLanguages,
	currentLocale,
	currentPageId,
	shoppingCartItemCount,
	isOpen,
	getPagePathFromId,
}) => {
	const { defaultLocale } = meta;

	const {
		background,
		settings,
		fontWeight,
		fontFamily,
		navLinkTextColor,
		navLinkTextColorHover,
		mobile,
		desktop,
		isExpressCheckoutEnabled,
	} = blocks.header;

	const {
		imageOrigin,
		imagePath,
	} = background;

	const {
		cartText,
		isCartVisible,
		isLanguageSwitcherHidden,
		isSticky,
		logoPlacement,
		navigationPlacement,
		showLogo,
		styles,
		logoImageOrigin,
		logoImagePath,
		logoSvg,
		headerLayout,
		mHeaderLayout,
		isMobileMenuDropdownFullScreen,
		mobileLinksAlignment,
		selectedLogoType,
		logoText,
	} = settings;

	const headerCSSVars = {
		...objectToCssVariablesDeprecated(styles),
		...getBackgroundStyles(background),
	};

	const getLogoSrc = () => {
		const logoWidth = Number.parseInt(styles['logo-width'], 10);

		return getOptimizedSrc(logoImageOrigin, logoImagePath, siteId, {
			// * 2 to upscale logo and make it look better on retina screens
			width: logoWidth * 2,
			quality: 95,
		});
	};

	const getHeaderBackgroundImageUrl = () => getOptimizedSrc(imageOrigin, imagePath, siteId, {
		width: FULL_WIDTH,
	});

	const mapNavItem = (navItem) => {
		const {
			navItemId,
			linkType,
			linkedPageId,
			isHidden,
			rel,
			href,
			target,
			name,
			subItems = [],
		} = navItem;

		return {
			navItemId,
			hasDropdown: !!subItems.length,
			isCurrent: currentPageId === linkedPageId,
			subItems: subItems.map(mapNavItem),
			target,
			name: linkType === NAVIGATION_PAGE_TYPE ? pages[linkedPageId]?.name : name,
			isHidden,
			rel,
			href: linkType === NAVIGATION_PAGE_TYPE ? getPagePathFromId({
				pageId: linkedPageId,
			}) : href,
			locale: currentLocale,
		};
	};

	const navItems = nav.filter(({ isHidden }) => !isHidden).map(mapNavItem);

	const isCartEnabled = isCartVisible && meta.ecommerceType === 'zyro'; // !ecommerceType is legacy flag but used in half of ecommerce flow checks, so do not remove

	const isLanguageSwitcherVisible = !!languageSwitcherLanguages.length
		&& !isLanguageSwitcherHidden
		&& currentLocale !== SYSTEM_LOCALE;

	const siteName = languageMetaTitle ?? meta.metaTitle;

	const socialIconsElementId = blocks.header.components.find((elementId) => elements[elementId].type === ELEMENT_TYPE_SOCIAL_ICONS);
	const socialIconsLinks = elements[socialIconsElementId]?.links || [];
	const socialIconsStyles = elements[socialIconsElementId]?.settings?.styles || {};

	const buttonElementId = blocks.header.components.find((elementId) => elements[elementId].type === ELEMENT_TYPE_BUTTON);
	const buttonElementData = elements[buttonElementId];

	return {
		background,
		backgroundColor: background.color,
		backgroundImageUrl: getHeaderBackgroundImageUrl(),
		backgroundColorContrast: styles.contrastBackgroundColor,
		cartText,
		cartIconSize: styles.cartIconSize,
		currentLocale,
		height: desktop?.height,
		heightMobile: mobile?.height,
		fontFamily,
		navLinkTextColor,
		navLinkTextColorHover,
		fontWeight,
		isTransparent: background.isTransparent,
		items: navItems,
		isCartVisible: isCartEnabled,
		itemsInCart: shoppingCartItemCount,
		isSticky,
		isLanguageSwitcherVisible,
		isLogoVisible: showLogo,
		isOpen,
		languageSwitcherLanguages,
		logoHeight: desktop?.logoHeight,
		logoHeightMobile: mobile?.logoHeight,
		selectedLogoType,
		logoText,
		logoTextFontSize: styles.logoTextFontSize,
		mLogoTextFontSize: styles.mLogoTextFontSize,
		logoTextColor: styles.logoTextColor,
		logoTextFont: styles.logoTextFont,
		logoTextFontWeight: styles.logoTextFontWeight,
		logoHref: currentLocale !== defaultLocale && currentLocale !== SYSTEM_LOCALE ? `/${currentLocale}` : '/',
		logoMaxWidth: styles['logo-width'],
		logoMaxWidthMobile: styles['m-logo-width'],
		logoPlacement,
		logoPlacementMobile: settings['m-logoPlacement'] || logoPlacement,
		logoSrc: getLogoSrc(),
		logoSvg,
		style: headerCSSVars,
		siteName,
		socialIconsLinks,
		socialIconsStyles,
		buttonElementId,
		buttonElementData,
		navigationPlacement,
		navigationPlacementMobile: settings['m-navigationPlacement'] || navigationPlacement,
		headerLayout,
		mHeaderLayout,
		isMobileMenuDropdownFullScreen,
		mobileLinksAlignment,
		isExpressCheckoutEnabled,
	};
};
